import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import Input from '../../../components/ui/Input';
import Button from '../../../components/ui/Button';
import Select from '../../../components/ui/Select';
import Icon from '../../../components/AppIcon';
import { useAuth } from '../../../contexts/AuthContext';
import { userService } from '../../../services/userService';
import { supabase } from '../../../lib/supabase';

const CompanyTab = () => {
  const { t } = useTranslation();
  const { userProfile, activeCompanyId, isSuperAdmin } = useAuth();
  const canEdit = isSuperAdmin || userProfile?.role === 'admin';
  const [companyData, setCompanyData] = useState({
    company_name: '',
    domain: '',
    industry: 'technology',
    company_size: '50-200',
    website: '',
    address: '',
    city: '',
    state: '',
    zipCode: '',
    country: 'us',
    vtex_account_name: ''
  });
  const [resolvedCompanyId, setResolvedCompanyId] = useState(null);

  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isUploadingLogo, setIsUploadingLogo] = useState(false);
  const [logoUrl, setLogoUrl] = useState('');
  const [errors, setErrors] = useState({});
  const fileInputRef = useRef(null);

  const loadCompanyData = (company) => {
    setResolvedCompanyId(company.id);
    setLogoUrl(company.logo_url || '');
    setCompanyData({
      company_name: company.company_name || '',
      domain: company.domain || '',
      industry: company.industry || 'technology',
      company_size: company.company_size || '50-200',
      website: company.website || '',
      address: company.address || '',
      city: company.city || '',
      state: company.state || '',
      zipCode: company.zip_code || company.zipCode || '',
      country: company.country || 'us',
      vtex_account_name: company.vtex_account_name || ''
    });
  };

  useEffect(() => {
    if (userProfile?.companies) {
      loadCompanyData(userProfile.companies);
    } else if (activeCompanyId) {
      supabase
        .from('companies')
        .select('*')
        .eq('id', activeCompanyId)
        .single()
        .then(({ data, error }) => {
          if (!error && data) loadCompanyData(data);
        });
    }
  }, [userProfile, activeCompanyId]);

  const industryOptions = [
    { value: 'technology', label: t('accountSettings.company.industries.technology') },
    { value: 'retail', label: t('accountSettings.company.industries.retail') },
    { value: 'manufacturing', label: t('accountSettings.company.industries.manufacturing') },
    { value: 'healthcare', label: t('accountSettings.company.industries.healthcare') },
    { value: 'finance', label: t('accountSettings.company.industries.finance') },
    { value: 'education', label: t('accountSettings.company.industries.education') },
    { value: 'real-estate', label: t('accountSettings.company.industries.realEstate') },
    { value: 'hospitality', label: t('accountSettings.company.industries.hospitality') },
    { value: 'other', label: t('accountSettings.company.industries.other') }
  ];

  const companySizeOptions = [
    { value: '1-10', label: t('accountSettings.company.companySizes.1-10') },
    { value: '11-50', label: t('accountSettings.company.companySizes.11-50') },
    { value: '50-200', label: t('accountSettings.company.companySizes.50-200') },
    { value: '201-500', label: t('accountSettings.company.companySizes.201-500') },
    { value: '501-1000', label: t('accountSettings.company.companySizes.501-1000') },
    { value: '1000+', label: t('accountSettings.company.companySizes.1000+') }
  ];

  const countryOptions = [
    { value: 'us', label: t('accountSettings.company.countries.us') },
    { value: 'co', label: t('accountSettings.company.countries.co') }
  ];

  const handleLogoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const companyId = resolvedCompanyId;
    if (!companyId) return;

    const allowedTypes = ['image/jpeg', 'image/png', 'image/webp', 'image/svg+xml'];
    if (!allowedTypes.includes(file.type)) {
      setErrors(prev => ({ ...prev, logo: t('accountSettings.company.errors.logoInvalidType') }));
      return;
    }

    if (file.size > 2 * 1024 * 1024) {
      setErrors(prev => ({ ...prev, logo: t('accountSettings.company.errors.logoTooLarge') }));
      return;
    }

    setIsUploadingLogo(true);
    setErrors(prev => ({ ...prev, logo: '' }));
    try {
      const ext = file.name.split('.').pop();
      const filePath = `${companyId}/logo.${ext}`;

      const { error: uploadError } = await supabase.storage
        .from('company-logos')
        .upload(filePath, file, { upsert: true, cacheControl: '3600' });

      if (uploadError) throw uploadError;

      const { data: { publicUrl } } = supabase.storage
        .from('company-logos')
        .getPublicUrl(filePath);

      const urlWithCache = `${publicUrl}?t=${Date.now()}`;
      setLogoUrl(urlWithCache);

      await userService?.updateCompany(companyId, { logo_url: publicUrl });
    } catch (error) {
      console.error('Error uploading logo:', error);
      setErrors(prev => ({ ...prev, logo: t('accountSettings.company.errors.logoUploadFailed') }));
    } finally {
      setIsUploadingLogo(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleInputChange = (field, value) => {
    setCompanyData(prev => ({ ...prev, [field]: value }));
    if (errors?.[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!companyData?.company_name?.trim()) {
      newErrors.company_name = t('accountSettings.company.errors.companyNameRequired');
    }

    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const handleSave = async () => {
    if (validateForm()) {
      setIsSaving(true);
      setErrors({});
      try {
        const companyId = resolvedCompanyId;
        if (!companyId) {
          throw new Error(t('accountSettings.company.errors.companyIdNotFound'));
        }
        await userService?.updateCompany(companyId, companyData);
        setIsEditing(false);
      } catch (error) {
        setErrors({ submit: error?.message });
      } finally {
        setIsSaving(false);
      }
    }
  };

  return (
    <div className="space-y-6 md:space-y-8">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h2 className="text-xl md:text-2xl font-semibold text-foreground">{t('accountSettings.company.title')}</h2>
          <p className="text-sm md:text-base text-muted-foreground mt-1">
            {t('accountSettings.company.subtitle')}
          </p>
        </div>
        {!isEditing && canEdit && (
          <Button
            variant="outline"
            iconName="Edit"
            iconPosition="left"
            onClick={() => setIsEditing(true)}
            className="w-full sm:w-auto"
          >
            {t('accountSettings.company.editCompany')}
          </Button>
        )}
      </div>
      <div className="bg-card border border-border rounded-lg p-4 md:p-6 lg:p-8 space-y-6 md:space-y-8">
        <div>
          <h3 className="text-base md:text-lg font-semibold text-foreground mb-4 md:mb-6">
            {t('accountSettings.company.basicInformation')}
          </h3>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 md:gap-6">
            <Input
              label={t('accountSettings.company.companyName')}
              type="text"
              value={companyData?.company_name}
              onChange={(e) => handleInputChange('company_name', e?.target?.value)}
              error={errors?.company_name}
              disabled={!isEditing}
              required
            />

            <Input
              label={t('accountSettings.company.domain')}
              type="text"
              value={companyData?.domain}
              onChange={(e) => handleInputChange('domain', e?.target?.value)}
              error={errors?.domain}
              disabled={!isEditing}
              required
              description={t('accountSettings.company.domainDescription')}
            />

            <Select
              label={t('accountSettings.company.industry')}
              options={industryOptions}
              value={companyData?.industry}
              onChange={(value) => handleInputChange('industry', value)}
              disabled={!isEditing}
            />

            <Select
              label={t('accountSettings.company.companySize')}
              options={companySizeOptions}
              value={companyData?.company_size}
              onChange={(value) => handleInputChange('company_size', value)}
              disabled={!isEditing}
            />

            <Input
              label={t('accountSettings.company.website')}
              type="url"
              value={companyData?.website}
              onChange={(e) => handleInputChange('website', e?.target?.value)}
              disabled={!isEditing}
              className="lg:col-span-2"
            />
          </div>
        </div>

        <div className="border-t border-border pt-6 md:pt-8">
          <h3 className="text-base md:text-lg font-semibold text-foreground mb-1 md:mb-2">
            {t('accountSettings.company.integrationsTitle')}
          </h3>
          <p className="text-sm text-muted-foreground mb-4 md:mb-6">
            {t('accountSettings.company.integrationsDesc')}
          </p>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 md:gap-6">
            <Input
              label={t('accountSettings.company.vtexAccountName')}
              type="text"
              value={companyData?.vtex_account_name}
              onChange={(e) => handleInputChange('vtex_account_name', e?.target?.value)}
              disabled={!isEditing}
              placeholder="mystore"
              description={t('accountSettings.company.vtexAccountNameDesc')}
            />
          </div>
        </div>

        <div className="border-t border-border pt-6 md:pt-8">
          <h3 className="text-base md:text-lg font-semibold text-foreground mb-4 md:mb-6">
            {t('accountSettings.company.companyAddress')}
          </h3>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 md:gap-6">
            <Input
              label={t('accountSettings.company.streetAddress')}
              type="text"
              value={companyData?.address}
              onChange={(e) => handleInputChange('address', e?.target?.value)}
              disabled={!isEditing}
              className="lg:col-span-2"
            />

            <Input
              label={t('accountSettings.company.city')}
              type="text"
              value={companyData?.city}
              onChange={(e) => handleInputChange('city', e?.target?.value)}
              disabled={!isEditing}
            />

            <Input
              label={t('accountSettings.company.state')}
              type="text"
              value={companyData?.state}
              onChange={(e) => handleInputChange('state', e?.target?.value)}
              disabled={!isEditing}
            />

            <Input
              label={t('accountSettings.company.zipCode')}
              type="text"
              value={companyData?.zipCode}
              onChange={(e) => handleInputChange('zipCode', e?.target?.value)}
              disabled={!isEditing}
            />

            <Select
              label={t('accountSettings.company.country')}
              options={countryOptions}
              value={companyData?.country}
              onChange={(value) => handleInputChange('country', value)}
              disabled={!isEditing}
            />
          </div>
        </div>

        <div className="border-t border-border pt-6 md:pt-8">
          <h3 className="text-base md:text-lg font-semibold text-foreground mb-4">
            {t('accountSettings.company.branding')}
          </h3>
          <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4 md:gap-6">
            <div className="w-20 h-20 md:w-24 md:h-24 rounded-lg bg-accent/20 flex items-center justify-center overflow-hidden flex-shrink-0">
              {logoUrl ? (
                <img src={logoUrl} alt="Company logo" className="w-full h-full object-contain" />
              ) : (
                <Icon name="Building2" size={32} className="text-accent" />
              )}
            </div>
            <div className="flex flex-col gap-2">
              <input
                ref={fileInputRef}
                type="file"
                accept="image/jpeg,image/png,image/webp,image/svg+xml"
                className="hidden"
                onChange={handleLogoUpload}
              />
              {isEditing && (
                <Button
                  variant="outline"
                  size="sm"
                  iconName={isUploadingLogo ? 'Loader' : 'Upload'}
                  iconPosition="left"
                  loading={isUploadingLogo}
                  disabled={isUploadingLogo}
                  onClick={() => fileInputRef.current?.click()}
                >
                  {isUploadingLogo ? t('common.loading') : t('accountSettings.company.uploadLogo')}
                </Button>
              )}
              {errors?.logo && (
                <p className="text-xs text-destructive">{errors.logo}</p>
              )}
              {isEditing && (
                <p className="text-xs text-muted-foreground">
                  {t('accountSettings.company.logoRecommendation')}
                </p>
              )}
            </div>
          </div>
        </div>

        {isEditing && (
          <div className="flex flex-col sm:flex-row gap-3 pt-4">
            {errors?.submit && (
              <p className="w-full text-sm text-destructive">{errors.submit}</p>
            )}
            <Button
              variant="default"
              onClick={handleSave}
              loading={isSaving}
              iconName="Check"
              iconPosition="left"
              className="w-full sm:w-auto"
            >
              {t('accountSettings.company.saveChanges')}
            </Button>
            <Button
              variant="outline"
              onClick={() => setIsEditing(false)}
              disabled={isSaving}
              className="w-full sm:w-auto"
            >
              {t('accountSettings.company.cancel')}
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};

export default CompanyTab;